import { useEffect, useState, type ReactElement } from 'react';
import { apiFetch, ApiError, useSession } from '../../auth';
import { EmberDisc } from '../components/EmberDisc';
import { PageBack } from '../components/PageBack';
import { Link } from '../components/Link';
import { effectiveDateLabel } from '../components/LegalUpdateNotice';
import { trackFailed } from '../../../../../shared-web/track';
import './LegalUpdate.css';

/**
 * /legal/update — what changed in the Terms of Service and Privacy Policy, in
 * plain words (2026-10-15 拍板; counsel's reply of 2026-10-14). Reached from the
 * update text (sms-notification-worker, notice legal_update) and from the horn
 * on the first screen. Telling is notice, not a wall: continuing to use Ember is
 * the agreement (Terms §1), nothing is refused, and the page can be left without
 * an answer. Someone who can answer — an account, or a guest browser that
 * ticked the boxes at a door — is offered two: "I agree" moves their standing
 * (legal.terms_accepted door notice); "I don't agree" moves nothing, is
 * recorded (legal.terms_declined), and says what it means: the updated terms
 * apply if they keep using Ember, they can leave (an account: delete it), and
 * the one new use can be turned off on its own, right there. Server:
 * edge/routes/legal-update-routes.js.
 */

interface UpdateState {
  version: string;
  who: 'account' | 'guest';
  can_answer: boolean;
  agreed: boolean;
  background_on: boolean;
}

type Answer = 'none' | 'agreed' | 'declined';

function reason(err: unknown): string {
  return err instanceof ApiError
    ? String((err.body as { error?: unknown } | null)?.error || `http_${err.status}`)
    : 'network_error';
}

export function LegalUpdatePage(): ReactElement {
  const { refresh } = useSession();
  const [state, setState] = useState<UpdateState | null>(null);
  const [answer, setAnswer] = useState<Answer>('none');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    apiFetch<UpdateState>('/api/legal/update')
      .then((s) => { if (live) { setState(s); if (s.agreed) setAnswer('agreed'); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const send = async (value: 'agree' | 'decline'): Promise<void> => {
    if (!state || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      await apiFetch('/api/legal/update/answer', { method: 'POST', body: { answer: value, version: state.version } });
      setAnswer(value === 'agree' ? 'agreed' : 'declined');
      if (state.who === 'account') void refresh();
    } catch (err) {
      trackFailed('legal_update_answer', reason(err));
      setFailed(true);
    }
    setBusy(false);
  };

  const turnOff = async (): Promise<void> => {
    if (!state || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      if (state.who === 'account') {
        await apiFetch('/api/me/privacy', { method: 'PUT', body: { background_experience: false } });
        void refresh();
      } else {
        await apiFetch('/api/legal/update/background-off', { method: 'POST' });
      }
      setState({ ...state, background_on: false });
    } catch (err) {
      trackFailed('legal_update_background_off', reason(err));
      setFailed(true);
    }
    setBusy(false);
  };

  const version = state?.version || '';
  return (
    <div className="ember-container legal-update-page" data-log-seen="legal_update" data-log-state={version}>
      <div className="legal-update-toolbar">
        <PageBack className="legal-update-back" />
      </div>

      <header className="legal-update-intro">
        <h1 className="legal-update-title"><EmberDisc face="warm" className="legal-update-disc" /><span>We updated our Terms and Privacy Policy</span></h1>
        <p className="legal-update-lede">
          {version ? `They take effect ${effectiveDateLabel(version)}. ` : ''}Here’s what changed, in plain words.
        </p>
      </header>

      <section className="legal-update-points" aria-label="What changed">
        <article>
          <h2>What you tell can help someone else — as a short note, never your words.</h2>
          <p>
            From what you tell, Ember writes short notes about where you stood in a situation, and how it went if
            you tell it later. With your name and anything that could point to you left out, those notes can help
            Ember understand someone else going through something similar.
          </p>
        </article>
        <article>
          <h2>Some things are never used this way.</h2>
          <p>
            A note that touches your health, sex life, religion, race, gender identity or immigration status is
            never used for anyone else. Neither is a story someone could recognize as yours.
          </p>
        </article>
        <article>
          <h2>One switch turns it off.</h2>
          <p>
            {state?.who === 'guest'
              ? 'It’s “Help Ember understand similar situations” in Settings once you join. Talking without an account? Email contact@oriscen.ai and we’ll turn it off for you.'
              : 'It’s “Help Ember understand similar situations” in Settings. Turn it off and nothing you tell is used for anyone else from then on.'}
          </p>
        </article>
        <article>
          <h2>Sharing an Ember works as you know it.</h2>
          <p>
            If one of your Embers could help someone, Ember asks you once. Only with your yes can someone in a
            similar place read it, and never with your name.
          </p>
        </article>
        <p className="legal-update-also">
          Earlier this month we also added rooms — private group chats Ember hosts, which you join only if you
          choose to — and public pages for each kind of situation, built only from what can’t be traced to anyone.
        </p>
        <p className="legal-update-docs">
          <a href="/terms" target="_blank" rel="noopener noreferrer" data-log="legal_update_terms">Read the Terms</a>
          <span aria-hidden> · </span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" data-log="legal_update_privacy">Read the Privacy Policy</a>
        </p>
      </section>

      {state && state.can_answer ? (
        <section className="legal-update-answer" aria-live="polite">
          {answer === 'none' ? (
            <div className="legal-update-actions">
              <button type="button" className="ember-btn ember-btn-primary" data-log="legal_update_agree" disabled={busy} onClick={() => { void send('agree'); }}>
                I agree
              </button>
              <button type="button" className="ember-btn ember-btn-secondary" data-log="legal_update_decline" disabled={busy} onClick={() => { void send('decline'); }}>
                I don’t agree
              </button>
            </div>
          ) : null}

          {answer === 'agreed' ? (
            <div className="legal-update-done" role="status">
              <p>You’re all set — thanks for reading.</p>
              <Link to="/" className="ember-text-action">Back to Ember</Link>
            </div>
          ) : null}

          {answer === 'declined' ? (
            <div className="legal-update-done" role="status">
              <p>That’s okay — thanks for telling us.</p>
              <p>
                The updated terms apply if you keep using Ember.{' '}
                {state.who === 'account'
                  ? <>If you’d rather not go on under them, you can <a href="/delete-account" data-log="legal_update_delete_account">delete your account</a> anytime.</>
                  : 'If you’d rather not go on under them, you can stop using Ember anytime.'}
              </p>
              {state.background_on ? (
                <>
                  <p>If what you mind is your experience helping others, you can turn just that off:</p>
                  <button type="button" className="ember-btn ember-btn-secondary" data-log="legal_update_background_off" disabled={busy} onClick={() => { void turnOff(); }}>
                    Turn it off
                  </button>
                </>
              ) : (
                <p>Your experience is kept out of what Ember tells anyone else.</p>
              )}
            </div>
          ) : null}
        </section>
      ) : null}

      {state && !state.can_answer ? (
        <p className="legal-update-note">These apply once you start talking with Ember.</p>
      ) : null}

      {failed ? (
        <p className="legal-update-error" role="alert">That didn’t go through. Please try again in a moment.</p>
      ) : null}
    </div>
  );
}
